import { useState } from 'react';
import { buyerAddress, buyerIdLabel } from '../lib/taxes.ts';
import { createPortal } from 'react-dom';
import { Button, Dialog, Field, Skeleton } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { saveBlob } from '../lib/api.ts';
import { LookPicker, PaperEnd, PaperLogoRight, paperAttrs, PaperSeller, useDocLook, type PaperLook } from '../lib/doclook.tsx';
import { day, money, todayIso } from '../lib/format.ts';
import { docName } from './Invoices.tsx';
import { useAction } from './manage/common.tsx';

type Kind = 'invoice' | 'credit';
const AGES = ['current', 'd30', 'd60', 'd90', 'older'] as const;

export interface StatementData {
  customer: { id: string; name: string; tax_id: string | null; id_type: string | null; address: string | null; town: string | null; country: string };
  company: { legal_name: string; trade_name: string | null; tax_id: string | null; address: string | null; city: string | null; phone: string | null; email: string | null };
  from: string;
  to: string;
  opening: number;
  entries: { kind: 'invoice' | 'credit' | 'payment' | 'refund'; date: string; document: string; doc_kind: Kind; number: number; year: number; ref: string | null; due_date: string | null; method: string | null; debit: number; credit: number; balance: number }[];
  debits: number;
  credits: number;
  closing: number;
  ages: Record<(typeof AGES)[number], number>;
  open: { id: string; kind: Kind; number: number; year: number; date: string; due_date: string | null; total: number; open: number; days_late: number }[];
}

const entryText = (e: StatementData['entries'][number]) => {
  const doc = docName({ kind: e.doc_kind, number: e.number, year: e.year });
  if (e.kind === 'payment') return t('st.e_payment', { doc, method: t(`inv.pm_${e.method}` as MessageKey) });
  if (e.kind === 'refund') return t('st.e_refund', { doc, method: t(`inv.pm_${e.method}` as MessageKey) });
  return doc;
};

/** What a customer owed at the start of a period, everything in between, and what it owes at the end. */
export function StatementDialog({ customer, name, onClose }: { customer: string; name: string; onClose: () => void }) {
  const today = todayIso();
  const [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(today);
  const valid = !!from && !!to && from <= to;
  const [period, setPeriod] = useState({ from, to });
  const { data } = useAction<StatementData>('invoicing.statement', { customer, ...period });
  const [printing, setPrinting] = useState(false);
  const look = useDocLook('statement');
  const print = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const csv = () => {
    if (!data) return;
    const cell = (v: string | number) => (typeof v === 'number' ? (v / 100).toFixed(2) : `"${v.replace(/"/g, '""')}"`);
    const rows = [
      [t('quote.date'), t('st.document'), t('st.due'), t('st.debit'), t('st.credit'), t('st.balance')].map(cell),
      [cell(data.from), cell(t('st.opening')), cell(''), cell(''), cell(''), cell(data.opening)],
      ...data.entries.map((e) => [cell(e.date), cell(entryText(e)), cell(e.due_date ?? ''), cell(e.debit), cell(e.credit), cell(e.balance)]),
      [cell(data.to), cell(t('st.closing')), cell(''), cell(data.debits), cell(data.credits), cell(data.closing)],
    ];
    void saveBlob(new Blob([`﻿${rows.map((r) => r.join(',')).join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' }), `${t('st.file')}-${name.replace(/[^\p{L}\p{N}]+/gu, '-')}-${data.to}.csv`);
  };
  return (
    <Dialog
      title={t('st.title', { name })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            <Button icon="download" onClick={csv} disabled={!data}>
              {t('st.csv')}
            </Button>
            <LookPicker {...look} />
          </span>
          <Button variant="primary" icon="printer" onClick={print} disabled={!data}>
            {t('inv.print')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <form
          className="st-period"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setPeriod({ from, to });
          }}
        >
          <Field label={t('st.from')}>
            <input className="input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('st.to')} error={valid ? null : t('st.bad_period')}>
            <input className="input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Button type="submit" icon="check" disabled={!valid || (from === period.from && to === period.to)}>
            {t('st.show')}
          </Button>
        </form>
        {!data ? (
          <Skeleton h={320} />
        ) : (
          <>
            <div className="item-summary">
              <div>
                <span className="muted">{t('st.opening')}</span>
                <strong>{money(data.opening)}</strong>
              </div>
              <div>
                <span className="muted">{t('st.invoiced')}</span>
                <strong>{money(data.debits)}</strong>
              </div>
              <div>
                <span className="muted">{t('st.paid')}</span>
                <strong>{money(data.credits)}</strong>
              </div>
              <div>
                <span className="muted">{t('st.closing')}</span>
                <strong>{money(data.closing)}</strong>
              </div>
            </div>
            <div className="inv-preview">
              <StatementPaper d={data} look={look.paper} />
            </div>
          </>
        )}
      </div>
      {printing && data
        ? createPortal(
            <div className="print-sheet print-invoice">
              <StatementPaper d={data} look={look.paper} />
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

export function StatementPaper({ d, look }: { d: StatementData; look?: PaperLook | null }) {
  const c = d.company;
  const b = d.customer;
  return (
    <article {...paperAttrs(look)} aria-label={t('st.p_title')}>
      <header className="inv-paper-head">
        <PaperSeller c={c} p={look} />
        <div className="inv-paper-title">
          <PaperLogoRight p={look} />
          <h2>{t('st.p_title')}</h2>
          <dl>
            <dt>{t('st.period')}</dt>
            <dd>
              {day(d.from)} – {day(d.to)}
            </dd>
          </dl>
        </div>
      </header>
      <section className="inv-paper-parties">
        <div>
          <p className="inv-label">{t('st.p_customer')}</p>
          <p className="strong">{b.name}</p>
          {b.tax_id ? <p>{t('inv.p_id', { type: buyerIdLabel(b), id: b.tax_id })}</p> : null}
          <p>{buyerAddress(b)}</p>
        </div>
      </section>
      <table className="inv-paper-lines">
        <thead>
          <tr>
            <th>{t('quote.date')}</th>
            <th>{t('st.document')}</th>
            <th>{t('st.due')}</th>
            <th className="num">{t('st.debit')}</th>
            <th className="num">{t('st.credit')}</th>
            <th className="num">{t('st.balance')}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="st-sum">
            <td className="st-day">{day(d.from)}</td>
            <td colSpan={4}>{t('st.opening')}</td>
            <td className="num">{money(d.opening)}</td>
          </tr>
          {d.entries.map((e, i) => (
            <tr key={i}>
              <td className="st-day">{day(e.date)}</td>
              <td>
                {entryText(e)}
                {e.ref ? <span className="muted"> · {e.ref}</span> : null}
              </td>
              <td className="st-day">{e.due_date ? day(e.due_date) : ''}</td>
              <td className="num">{e.debit ? money(e.debit) : ''}</td>
              <td className="num">{e.credit ? money(e.credit) : ''}</td>
              <td className="num">{money(e.balance)}</td>
            </tr>
          ))}
          {!d.entries.length ? (
            <tr>
              <td colSpan={6} className="muted">
                {t('st.nothing')}
              </td>
            </tr>
          ) : null}
          <tr className="st-sum">
            <td className="st-day">{day(d.to)}</td>
            <td colSpan={2}>{t('st.closing')}</td>
            <td className="num">{money(d.debits)}</td>
            <td className="num">{money(d.credits)}</td>
            <td className="num">{money(d.closing)}</td>
          </tr>
        </tbody>
      </table>
      <section aria-label={t('st.ageing')}>
        <p className="inv-label">{t('st.ageing_on', { day: day(d.to) })}</p>
        <dl className="st-ages">
          {AGES.map((a) => (
            <div key={a} className={a !== 'current' && d.ages[a] > 0 ? 'late' : ''}>
              <dt>{t(`st.a_${a}`)}</dt>
              <dd>{money(d.ages[a])}</dd>
            </div>
          ))}
        </dl>
      </section>
      {d.open.length ? (
        <table className="inv-paper-lines">
          <thead>
            <tr>
              <th>{t('st.open_docs')}</th>
              <th>{t('quote.date')}</th>
              <th>{t('st.due')}</th>
              <th className="num">{t('st.late')}</th>
              <th className="num">{t('inv.total')}</th>
              <th className="num">{t('st.open')}</th>
            </tr>
          </thead>
          <tbody>
            {d.open.map((o) => (
              <tr key={o.id}>
                <td>{docName(o)}</td>
                <td className="st-day">{day(o.date)}</td>
                <td className="st-day">{o.due_date ? day(o.due_date) : ''}</td>
                <td className="num">{o.days_late ? tn('st.days', o.days_late) : ''}</td>
                <td className="num">{money(o.total)}</td>
                <td className="num">{money(o.open)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <p className="muted">{t('st.p_note')}</p>
      <PaperEnd p={look} buyerLabel={t('look.p_confirmed_sign')} />
    </article>
  );
}
